/**
 * Utility functions for handling LinkedIn URLs
 */

/**
 * Validates if a string is a valid LinkedIn profile URL
 * @param url - The URL to validate
 * @returns true if it's a valid LinkedIn URL, false otherwise
 */
function isValidLinkedInUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  
  // Remove whitespace
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return false;
  
  try {
    // Check if it's already a LinkedIn URL (with or without protocol)
    if (trimmedUrl.includes('linkedin.com/in/')) {
      let urlToCheck = trimmedUrl;
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        urlToCheck = `https://${trimmedUrl}`;
      }
      
      const urlObj = new URL(urlToCheck);
      
      // Check if it's a LinkedIn domain
      const linkedinDomains = ['linkedin.com', 'www.linkedin.com'];
      if (!linkedinDomains.includes(urlObj.hostname)) {
        return false;
      }
      
      // Check if it's a profile URL (should contain /in/)
      if (!urlObj.pathname.includes('/in/')) {
        return false;
      }
      
      return true;
    }
    
    // If it doesn't start with http and doesn't contain linkedin.com, assume it's a profile identifier
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      const urlToCheck = `https://linkedin.com/in/${trimmedUrl}`;
      const urlObj = new URL(urlToCheck);
      
      // Check if it's a LinkedIn domain
      const linkedinDomains = ['linkedin.com', 'www.linkedin.com'];
      if (!linkedinDomains.includes(urlObj.hostname)) {
        return false;
      }
      
      return true;
    }
    
    // If it starts with http, validate it directly
    const urlObj = new URL(trimmedUrl);
    
    // Check if it's a LinkedIn domain
    const linkedinDomains = ['linkedin.com', 'www.linkedin.com'];
    if (!linkedinDomains.includes(urlObj.hostname)) {
      return false;
    }
    
    // Check if it's a profile URL (should contain /in/)
    if (!urlObj.pathname.includes('/in/')) {
      return false;
    }
    
    return true;
  } catch {
    // If URL parsing fails, it's not a valid URL
    return false;
  }
}

/**
 * Formats a LinkedIn URL to ensure it's a proper LinkedIn profile URL
 * @param linkedinValue - The LinkedIn value (could be URL, profile ID, etc.)
 * @returns A properly formatted LinkedIn URL or null if invalid
 */
export function formatLinkedInUrl(linkedinValue: string | null | undefined): string | null {
  if (!linkedinValue || typeof linkedinValue !== 'string') {
    return null;
  }
  
  const trimmedValue = linkedinValue.trim();
  if (!trimmedValue) return null;
  
  // Check if it's already a LinkedIn URL (with or without protocol)
  if (trimmedValue.includes('linkedin.com/in/')) {
    // It's already a LinkedIn URL, just ensure it has https protocol
    if (!trimmedValue.startsWith('http://') && !trimmedValue.startsWith('https://')) {
      return `https://${trimmedValue}`;
    }
    // Ensure it uses https
    if (trimmedValue.startsWith('http://')) {
      return trimmedValue.replace('http://', 'https://');
    }
    return trimmedValue;
  }
  
  // If it doesn't start with http and doesn't contain linkedin.com, assume it's a profile identifier
  if (!trimmedValue.startsWith('http://') && !trimmedValue.startsWith('https://')) {
    // Remove any leading slashes or @ symbols
    const cleanProfileId = trimmedValue.replace(/^[\/@]+/, '');
    if (cleanProfileId) {
      return `https://linkedin.com/in/${cleanProfileId}`;
    }
  }
  
  // If it's already a valid LinkedIn URL, return it
  if (isValidLinkedInUrl(trimmedValue)) {
    // Ensure it uses https
    if (trimmedValue.startsWith('http://')) {
      return trimmedValue.replace('http://', 'https://');
    }
    return trimmedValue;
  }
  
  return null;
}

/**
 * Gets the display text for a LinkedIn URL
 * @param linkedinValue - The LinkedIn value
 * @returns Display text for the LinkedIn link
 */
export function getLinkedInDisplayText(linkedinValue: string | null | undefined): string {
  if (!linkedinValue) return 'LinkedIn Profile';
  
  const formattedUrl = formatLinkedInUrl(linkedinValue);
  if (!formattedUrl) return 'LinkedIn Profile';
  
  try {
    const urlObj = new URL(formattedUrl);
    const pathParts = urlObj.pathname.split('/');
    const profileId = pathParts[pathParts.length - 1];
    
    if (profileId && profileId !== 'in') {
      return `LinkedIn Profile (${profileId})`;
    }
  } catch {
    // If URL parsing fails, return default text
  }
  
  return 'LinkedIn Profile';
} 
export const LINKEDIN_NOT_CONNECTED = 'UNIPILE_NOT_CONNECTED';

/**
 * Reads the { error, code } body returned by the fetch-linkedin-profile edge function
 * from a supabase.functions.invoke error (FunctionsHttpError keeps the Response in `context`)
 * @param error - The error returned by supabase.functions.invoke
 * @returns The error code and message sent by the function, when available
 */
export async function readLinkedInFetchError(error: unknown): Promise<{ code: string | null; message: string | null }> {
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response))
    return { code: null, message: null };

  try {
    const body = await context.clone().json();
    return {
      code: typeof body?.code === 'string' ? body.code : null,
      message: typeof body?.error === 'string' ? body.error : null
    };
  } catch {
    return { code: null, message: null };
  }
}
